/** Tableau clients : INSEE, nom d'affichage, e-mail, SIRET, solde, payé, création, actions. */
import { formatWhen } from "../../formats/display";
import { useFormatters } from "../../formats/useFormatters";
import { ListPager, usePagedRows } from "../../components/ListPager";
import { SortTh, sortRows, useTableSort } from "../../components/tableSort";
import { formatBalance } from "./format";
import { ClientActions } from "./ClientActions";
import { CopyValue } from "./CopyValue";
import { SirenePill, sireneLabel } from "./SireneBadge";
import { SiretValue } from "./SiretValue";
import "./sirene.css";
import "./tables.css";
import type { ClientListItem } from "./types";

function listName(c: ClientListItem): string {
  const trade = (c.display_name || "").trim();
  return trade !== "" ? trade : c.name;
}

type ClientTableProps = {
  rows: ClientListItem[];
  emptyText?: string;
  menuId: number | null;
  onMenu: (id: number | null) => void;
  onOpen: (id: number) => void;
  onEdit: (id: number) => void;
  onStatement: (id: number) => void;
  onArchive: (row: ClientListItem) => void;
  onDelete: (row: ClientListItem) => void;
};

export function ClientTable({
  rows,
  emptyText = "Aucun client.",
  menuId,
  onMenu,
  onOpen,
  onEdit,
  onStatement,
  onArchive,
  onDelete,
}: ClientTableProps) {
  const { money, day, formats } = useFormatters();
  const sort = useTableSort<"insee" | "name" | "email" | "siret" | "balance" | "paid" | "seen" | "created">();
  const shown = sort.key
    ? sortRows(rows, sort.dir, sort.key === "created" || sort.key === "seen" ? "date" : sort.key === "balance" || sort.key === "paid" ? "num" : "text", (c) => {
        if (sort.key === "insee") return sireneLabel(c.sirene_status);
        if (sort.key === "name") return listName(c);
        if (sort.key === "email") return c.email || "";
        if (sort.key === "siret") return c.siret || "";
        if (sort.key === "balance") return c.balance_cents;
        if (sort.key === "paid") return c.paid_to_date_cents;
        if (sort.key === "seen") return c.portal_last_seen_at || "";
        return c.created_at || "";
      })
    : rows;
  const paged = usePagedRows(shown);
  if (rows.length === 0) {
    return (
      <div className="fx-clients-wrap">
        <p className="fx-clients-empty">{emptyText}</p>
      </div>
    );
  }
  return (
    <div className="fx-clients-wrap">
      <table className="fx-clients-table">
        <thead>
          <tr>
            <SortTh className="fx-clients-table__insee" label="INSEE" active={sort.key === "insee"} dir={sort.dir} onSort={() => sort.onSort("insee", "text")} />
            <SortTh label="Nom d’affichage" active={sort.key === "name"} dir={sort.dir} onSort={() => sort.onSort("name", "text")} />
            <SortTh label="E-mail" active={sort.key === "email"} dir={sort.dir} onSort={() => sort.onSort("email", "text")} />
            <SortTh label="SIRET" active={sort.key === "siret"} dir={sort.dir} onSort={() => sort.onSort("siret", "text")} />
            <SortTh className="is-num" label="Solde" active={sort.key === "balance"} dir={sort.dir} onSort={() => sort.onSort("balance", "num")} />
            <SortTh className="is-num" label="Payé à ce jour" active={sort.key === "paid"} dir={sort.dir} onSort={() => sort.onSort("paid", "num")} />
            <SortTh label="Dernière connexion" active={sort.key === "seen"} dir={sort.dir} onSort={() => sort.onSort("seen", "date")} />
            <SortTh label="Créé le" active={sort.key === "created"} dir={sort.dir} onSort={() => sort.onSort("created", "date")} />
            <th className="is-act" scope="col">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {paged.slice.map((c) => (
            <tr
              key={c.id}
              className={`fx-clients-table__row${c.archived ? " is-archived" : ""}`}
              onClick={() => onOpen(c.id)}
            >
              <td className="fx-clients-table__insee">
                <SirenePill
                  className="fx-sirene-pill--list"
                  status={c.sirene_status}
                  checkedAt={c.sirene_checked_at}
                  closedOn={c.sirene_closed_on}
                  createdOn={c.sirene_created_on}
                />
              </td>
              <td>
                <span className="fx-clients-table__id">
                  <span className="fx-clients-table__name">{listName(c)}</span>
                  {c.archived ? <span className="fx-clients-table__tag">Archivé</span> : null}
                </span>
                {c.contact_name ? <span className="fx-clients-table__meta">{c.contact_name}</span> : null}
              </td>
              <td className="fx-clients-table__muted">
                {c.email ? (
                  <CopyValue text={c.email} label="Copier l’e-mail">
                    {c.email}
                  </CopyValue>
                ) : (
                  <span className="fx-clients-table__miss">Aucun e-mail</span>
                )}
              </td>
              <td className="siret fx-clients-table__muted">
                <SiretValue value={c.siret} />
              </td>
              <td className={`is-num ${formatBalance(c.balance_cents, money).className}`}>{formatBalance(c.balance_cents, money).label}</td>
              <td className="is-num fx-clients-table__muted">{money(c.paid_to_date_cents)}</td>
              <td className="fx-clients-table__muted">{formatWhen(c.portal_last_seen_at, formats)}</td>
              <td className="fx-clients-table__muted">{day(c.created_at)}</td>
              <td className="is-act" onClick={(e) => e.stopPropagation()}>
                <ClientActions
                  client={c}
                  open={menuId === c.id}
                  onToggle={() => onMenu(menuId === c.id ? null : c.id)}
                  onClose={() => onMenu(null)}
                  onEdit={() => {
                    onMenu(null);
                    onEdit(c.id);
                  }}
                  onStatement={() => {
                    onMenu(null);
                    onStatement(c.id);
                  }}
                  onArchive={() => {
                    onMenu(null);
                    onArchive(c);
                  }}
                  onDelete={() => {
                    onMenu(null);
                    onDelete(c);
                  }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <ListPager page={paged.page} pages={paged.pages} total={paged.total} from={paged.from} to={paged.to} setPage={paged.setPage} />
    </div>
  );
}
